import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable, ScrollView, Platform } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { fontStyle } from '../config/fonts';
import PixelContainer from './PixelContainer';

interface RightSidebarProps {
  onClose?: () => void;
}

export default function RightSidebar({ onClose }: RightSidebarProps) {
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState<'servers' | 'mods'>('servers');

  const placeholderServers = [
    { id: 1, name: 'Gaming Zone', members: '125K' },
    { id: 2, name: 'Tech Talk', members: '98K' },
    { id: 3, name: 'Art & Design', members: '87K' },
    { id: 4, name: 'Music Lovers', members: '76K' },
    { id: 5, name: 'Food & Cooking', members: '65K' },
  ];

  const placeholderMods = [
    { id: 1, name: 'Super Admin', zone: 'Gaming Zone' },
    { id: 2, name: 'Tech Moderator', zone: 'Tech Talk' },
    { id: 3, name: 'Art Curator', zone: 'Art & Design' },
    { id: 4, name: 'Music Guide', zone: 'Music Lovers' },
    { id: 5, name: 'Chef Master', zone: 'Food & Cooking' },
  ];

  return (
    <View style={[styles.container, { backgroundColor: colors.background, borderLeftColor: colors.border }]}>
      {/* Tab Header */}
      <View style={styles.tabHeader}>
        <Pressable
          style={[
            styles.tab,
            activeTab === 'servers' && [styles.activeTab, { borderBottomColor: colors.primary }]
          ]}
          onPress={() => setActiveTab('servers')}
        >
          <Text
            style={[
              styles.tabText,
              fontStyle,
              { color: activeTab === 'servers' ? colors.text : colors.textSecondary }
            ]}
          >
            Top Servers
          </Text>
        </Pressable>
        <Pressable
          style={[
            styles.tab,
            activeTab === 'mods' && [styles.activeTab, { borderBottomColor: colors.primary }]
          ]}
          onPress={() => setActiveTab('mods')}
        >
          <Text
            style={[
              styles.tabText,
              fontStyle,
              { color: activeTab === 'mods' ? colors.text : colors.textSecondary }
            ]}
          >
            Top Mods
          </Text>
        </Pressable>
      </View>

      {/* Tab Content */}
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
      >
        {activeTab === 'servers' ? (
          <View style={styles.listContainer}>
            {placeholderServers.map((server, index) => (
              <Pressable
                key={server.id}
                style={({ pressed }) => [
                  styles.listItem,
                  pressed && { backgroundColor: colors.surface }
                ]}
              >
                <View style={styles.rankContainer}>
                  <Text style={[styles.rankText, fontStyle, { color: colors.textSecondary }]}>
                    {index + 1}
                  </Text>
                </View>
                <View style={styles.itemContent}>
                  <Text style={[styles.itemName, fontStyle, { color: colors.text }]}>
                    {server.name}
                  </Text>
                  <Text style={[styles.itemMeta, fontStyle, { color: colors.textSecondary }]}>
                    {server.members} members
                  </Text>
                </View>
              </Pressable>
            ))}
          </View>
        ) : (
          <View style={styles.listContainer}>
            {placeholderMods.map((mod, index) => (
              <Pressable
                key={mod.id}
                style={({ pressed }) => [
                  styles.listItem,
                  pressed && { backgroundColor: colors.surface }
                ]}
              >
                <View style={styles.rankContainer}>
                  <Text style={[styles.rankText, fontStyle, { color: colors.textSecondary }]}>
                    {index + 1}
                  </Text>
                </View>
                <View style={styles.itemContent}>
                  <Text style={[styles.itemName, fontStyle, { color: colors.text }]}>
                    {mod.name}
                  </Text>
                  <Text style={[styles.itemMeta, fontStyle, { color: colors.textSecondary }]}>
                    {mod.zone}
                  </Text>
                </View>
              </Pressable>
            ))}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 300,
    borderLeftWidth: Platform.OS === 'web' ? 1 : 0,
    height: '100%',
  },
  tabHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomWidth: 2,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  listContainer: {
    gap: 8,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 12,
  },
  rankContainer: {
    width: 24,
    alignItems: 'center',
  },
  rankText: {
    fontSize: 14,
    fontWeight: '600',
  },
  itemContent: {
    flex: 1,
    gap: 4,
  },
  itemName: {
    fontSize: 14,
    fontWeight: '600',
  },
  itemMeta: {
    fontSize: 12,
  },
});
